import { Link, useParams } from 'react-router-dom';
import { categories, videos } from '@/mocks/data';
import VideoCard from '@/components/feature/VideoCard';
import { useState, useEffect } from 'react';

const categoryIcons: Record<string, string> = {
  dianying: 'ri-film-line',
  dianshiju: 'ri-tv-line',
  zongyi: 'ri-emotion-laugh-line',
  dongman: 'ri-ghost-smile-line',
  jilupian: 'ri-global-line',
  tiyu: 'ri-basketball-line',
  yinyue: 'ri-music-line',
  youxi: 'ri-gamepad-line',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [selectedSlug, setSelectedSlug] = useState(slug || 'dianying');
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  useEffect(() => {
    if (slug) {
      setSelectedSlug(slug);
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === selectedSlug) || categories[0];
  const filteredVideos = videos
    .filter((v) => v.category === selectedSlug)
    .sort((a, b) => {
      if (sortBy === 'new') return b.year - a.year;
      if (sortBy === 'rating') return b.rating - a.rating;
      return (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0);
    });

  const otherCategories = categories.filter((c) => c.slug !== selectedSlug);

  return (
    <>
      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${activeCategory.name} - 樱桃视频`,
            description: `樱桃视频${activeCategory.name}频道，提供最新最全的${activeCategory.name}资源，高清免费在线观看。`,
            url: `https://www.yingtao-video.com/category/${selectedSlug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '樱桃视频',
              url: 'https://www.yingtao-video.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.yingtao-video.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: activeCategory.name,
                item: `https://www.yingtao-video.com/category/${selectedSlug}`,
              },
            ],
          }),
        }}
      />

      {/* Breadcrumb */}
      <div className="bg-background-100 border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <nav className="flex items-center gap-2 text-sm" aria-label="面包屑导航">
            <Link to="/" className="text-foreground-400 hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-600 text-xs"></i>
            <span className="text-foreground-200 font-medium">{activeCategory.name}</span>
          </nav>
        </div>
      </div>

      {/* Category Header */}
      <section className="w-full bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/15">
              <i className={`${categoryIcons[activeCategory.id] || 'ri-film-line'} text-primary-400 text-2xl`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
                {activeCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredVideos.length} 部{activeCategory.name}资源
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs + Sort */}
      <section className="sticky top-16 z-40 bg-background-100/95 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between py-3 gap-4">
            <div className="flex items-center gap-1.5 overflow-x-auto flex-1 scrollbar-hide">
              {/* Current category active */}
              <Link
                to={`/category/${activeCategory.slug}`}
                className="px-4 py-2 text-sm rounded-full bg-primary-500 text-foreground-50 font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                {activeCategory.name}
              </Link>
              {otherCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-2 text-sm rounded-full bg-background-200/50 text-foreground-400 hover:text-foreground-200 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {[
                { key: 'hot', label: '热门' },
                { key: 'new', label: '最新' },
                { key: 'rating', label: '评分' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key as 'hot' | 'new' | 'rating')}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === s.key
                      ? 'bg-primary-500/20 text-primary-400 font-medium'
                      : 'text-foreground-400 hover:text-foreground-200'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video Grid */}
      <section className="w-full bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {filteredVideos.map((video) => (
                <Link key={video.id} to={`/article/${video.id}`} className="block">
                  <VideoCard video={video} />
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                <i className="ri-film-line text-foreground-500 text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无内容</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}